import { defineStore } from 'pinia'
import ImageDb from '@/stores/imageDB'
import { useImageStore } from '@/stores/imageStore'
import Customer from '@/models/Customer'
import Demographic from '@/models/Demographic'
import Economic from '@/models/Economic'
import { useSyncableModel } from '@/composables/useSyncableModel.js'
import { ref } from 'vue'
import { useApiResource } from '@/composables/resources/useApiResource.js'
import { useUserStore } from '@/stores/userStore'
import { searchMatcher, serverSearchTerm } from '@/helpers/search.js'

// How long opening a farmer's profile waits for a missing related record while online
const RELATED_RECORDS_WAIT_MS = 4000

export const useCustomerStore = defineStore('customerStore', () => {
  let syncableModel = useSyncableModel(useApiResource('customers'), {
    className: Customer.modelName.name,
    paramKey: Customer.modelName.paramKey,
    // The app only reads a farmer's whole name. Sending it back once stored the phone number the
    // server showed for a farmer with no name as their name.
    unsentFields: ['name']
  })

  let customer = ref(null)

  async function onlineSearchCustomers(searchText) {
    const term = serverSearchTerm(searchText, useUserStore().partner?.country)
    if (term == '') return []

    // Without a connection the server has nothing to add to the device's own results, which the
    // screens show first, so a failed request adds nothing rather than leaving the search spinning.
    try {
      const results = await syncableModel.resource.get(`/search`, { search_term: term })
      return results.data.map(customer => Object.assign(new Customer(), customer))
    } catch (error) {
      console.warn('Farmer search could not reach the server', error)
      return []
    }
  }

  function offlineSearchCustomers(searchText) {
    const matches = searchMatcher(searchText, useUserStore().partner?.country)
    let localCustomers = syncableModel.all()
    return localCustomers.filter((item) => matches({
      texts:  [item.name_cache, item.demographic()?.identity_number],
      phones: [item.msisdn]
    }))
  }

  // +relatedWaitMs+ is how long a farmer already on the phone waits for a demographic or economic
  // record the phone lacks
  async function checkCustomerLoaded(uuid, { relatedWaitMs = RELATED_RECORDS_WAIT_MS } = {}) {
    const obj = syncableModel.collection.value.find((item) => item.uuid == uuid)
    console.log("checkCustomerLoaded", uuid, obj)
    if (obj === undefined) {
      console.log("Customer not loaded, fetching:", uuid)
      const customerFetch = syncableModel.resource.get(uuid).then((resp) => {
        const customer_data = resp.data
        if (Object.hasOwn(customer_data, 'image_urls') && customer_data.image_urls.length > 0) {
          ImageDb.addUrlFromServer(customer_data.image_urls[1]).then((uuid) => {
            const imageStore = useImageStore()
            imageStore.collection.push({ type: 'customer', type_id: customer_data.uuid, uuid: uuid })
          })
        }
        syncableModel.collection.value.push({ ...customer_data, synced: true })
      })

      const demographicFetch = syncableModel.resource.get(`${uuid}/demographic`).then((resp) => {
        const demographic_data = resp.data
        Demographic.store().collection.push({ ...demographic_data, synced: true })
      })

      const economicFetch = syncableModel.resource.get(`${uuid}/economic`).then((resp) => {
        const economic_data = resp.data
        Economic.store().collection.push({ ...economic_data, synced: true })
      })

      await Promise.all([customerFetch, demographicFetch, economicFetch])
    } else {
      // The farmer is on this phone, so the profile can open whether or not the network is there.
      // A demographic or economic record the phone lacks (a farmer onboarded with only some of them)
      // is fetched while online and waited for only briefly; a failed fetch is not an error, because
      // offline the agent must still reach the farmer to map their fields.
      console.log("Customer already loaded:", uuid)
      const fetches = []
      const fetchQuietly = (promise) => promise.catch((error) => console.warn(`Could not fetch a record of farmer ${uuid}`, error))

      // Checked again on arrival: the profile may have been opened twice before the first fetch came back
      const hasDemographic = () => Demographic.store().collection.some((item) => item.customer_uuid === uuid)
      const hasEconomic = () => Economic.store().collection.some((item) => item.customer_uuid === uuid)

      if (!hasDemographic()) {
        fetches.push(fetchQuietly(syncableModel.resource.get(`${uuid}/demographic`).then((resp) => {
          const demographic_data = resp.data
          if (!hasDemographic()) Demographic.store().collection.push({ ...demographic_data, synced: true })
        })))
      }

      if (!hasEconomic()) {
        fetches.push(fetchQuietly(syncableModel.resource.get(`${uuid}/economic`).then((resp) => {
          const economic_data = resp.data
          if (!hasEconomic()) Economic.store().collection.push({ ...economic_data, synced: true })
        })))
      }

      if (fetches.length > 0 && navigator.onLine !== false) {
        await Promise.race([Promise.all(fetches), new Promise((resolve) => setTimeout(resolve, relatedWaitMs))])
      }
      return uuid
    }
  }

  function isOnDevice(uuid) {
    return syncableModel.collection.value.some((item) => item.uuid == uuid)
  }

  function $reset() {
    syncableModel.collection.value = []
    customer.value = null
  }

  return {
    ...syncableModel,
    customer,
    onlineSearchCustomers,
    offlineSearchCustomers,
    checkCustomerLoaded,
    isOnDevice,
    $reset
  }

}, {
  persist: true
})
